import * as React from 'react';
import ConfirmPopup from './ConfirmPopup';
import AllocateAddItemModal from './AllocateAddItemModal';
import DriverJobsPhotosModal from './DriverJobsPhotos';
import { toast } from 'react-toastify';
import { useState } from 'react';
import type { GenericList } from './models/JobSharedInterfaces';
import { DownloadFile, GetQuery, PostQuery } from './Shared';
import type { NewManualItem } from './models/JobScheduleInterfaces';
import type { DriverJob } from './models/DriverJobInterfaces';

interface DriverJobsListProps {
    truckIndex: number;
    truckId: number;
    truckJobList: TruckJobList[];
    truckOptions: GenericList[];
    scheduleTypes: GenericList[];
    updateTruckIndex(truckIndex: number): void;
    departOffice(): void;
    arriveOffice(): void;
    arriveSite(jobIndex: number): void;
    departSite(jobIndex: number): void;
    showSiteIssues(jobIndex: number): void;
    goToolCheck(): void;
    saveLunch(scheduleId: number, jobId: number): void;
    updateJobs(jobs: DriverJob[]): void;
    refreshJobs(): void;
}

interface TruckJobList {
    truckId: number;
    estimatedSiteTimeFormatted: string;
    actualSiteTimeFormatted: string;
    estimatedTravelTimeFormatted: string;
    actualTravelTimeFormatted: string;
    truckCheckComplete: boolean;
    truckToolCheckComplete: boolean;
    truckFinalToolCheckComplete: boolean;
    allJobsComplete: boolean;
    hasDepartedOffice: boolean;
    hasArrivedOffice: boolean;
    hadLunch: boolean;
    truckNotes: string;
    jobs: DriverJob[];
}


const DriverJobsListData = (props: DriverJobsListProps) => {
    const [showLunch, setShowLunch] = useState(false);
    const [showNewItemModal, setShowNewItemModal] = useState(false);
    const initNewItem: NewManualItem = {
        jobId: 0,
        siteAddress: "",
        googleAddress: "",
        googleLatitude: 0,
        googleLongitude: 0,
        customerName: "",
        contactName: "",
        scheduleDate: "",
        scheduleBranchId: 1,
        description: "",
        typeId: -1,
        itemTypeGroupId: -1,
        notes: "",
        runsheetNotes: "",
        errors: {}
    };
    const [newItem, setNewItem] = useState<NewManualItem>(initNewItem);
    const [showConfirmCancel, setShowConfirmCancel] = useState(false);
    const [cancelIndex, setCancelIndex] = useState(-1);
    const [showPhotoImport, setShowPhotoImport] = useState(false);
    const [jobId, setJobId] = useState(0);

    React.useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const updateTruck = (e: any) => {
        let truckId = e.target.value * 1;

        var truckOptions = props.truckOptions;
        var truckIndex = truckOptions.findIndex(t => t.id === truckId);

        props.updateTruckIndex(truckIndex);
    }

    const departOffice = () => {
        props.departOffice();
    }

    const arriveOffice = () => {
        props.arriveOffice();
    }

    const arriveSite = (e: any, jobIndex: number) => {
        e.preventDefault();

        props.arriveSite(jobIndex);
    }

    const departSite = (e: any, jobIndex: number) => {
        e.preventDefault();

        //check if install job and therefore must have at least 4 photos uploaded
        readyForDeparture(jobIndex);     
        //props.departSite(jobIndex);
    }

    const readyForDeparture = async (jobIndex: number) => {
        //if an install job then check to make sure at least 4 photos have been uploaded
        var truckJobList = props.truckJobList;
        var jobs = truckJobList[props.truckIndex].jobs;
        var job = jobs[jobIndex];

        var hasInstallItems = false;
        for (var i = 0; i < job.items.length; i++) {
            let item = job.items[i];
            if (item.typeId === 1) {
                hasInstallItems = true;
                break;
            }
        }

        if (hasInstallItems && !job.isAssistJob && !job.isManualJob) {
            //check if 4 photos uploaded
            const apiUrl = 'GetJobPhotoCount?JobId=' + job.jobId;
            var result = await GetQuery(apiUrl);

            if (result.error) {
                toast.error("Error getting data!");
            } else {
                var photoCount = result.data;
                if (photoCount < 4) {
                    //toast.error("Please upload at least 4 photos of the job before departing!");
                    toast.info("Remember to upload at least 4 photos to this job!");
                    props.departSite(jobIndex);
                } else {
                    props.departSite(jobIndex);
                }
            };
        } else {
            props.departSite(jobIndex);
        }
    }

    const showSiteIssues = (e: any, jobIndex: number) => {
        e.preventDefault();

        props.showSiteIssues(jobIndex);
    }

    const goToolCheck = (e: any) => {
        e.preventDefault();

        props.goToolCheck();
    }

    //lunch
    const addLunch = () => {
        setShowLunch(true);
    }

    const hideLunch = () => {
        setShowLunch(false);
    }

    const saveLunch = () => {
        //find job to add lunch break to
        var truckJobList = props.truckJobList;
        var jobs = truckJobList[props.truckIndex].jobs;
        let scheduleId = 0;
        let jobId = 0;
        var currentJobs = jobs.filter(j => j.hasArrived && !j.isComplete);
        if (currentJobs.length > 0) {
            //should only be one but get last one
            let currentJob = currentJobs[currentJobs.length - 1];
            scheduleId = currentJob.id;
            jobId = currentJob.jobId;
        };

        setShowLunch(false);
        props.saveLunch(scheduleId, jobId)
    }
    //end lunch

    //Upload Photos
    const showUploadPhotos = (e: any, itemIndex: number) => {
        e.preventDefault();

        var truckJobList = props.truckJobList;
        var jobs = truckJobList[props.truckIndex].jobs;
        var job = jobs[itemIndex];

        setShowPhotoImport(true);
        setJobId(job.jobId);
    }

    const hideUploadPhotos = () => {
        setShowPhotoImport(false);
        setJobId(0);
    }
    //End Upload Photos

    //Add New Item Modal 
    const hideNewItemModal = () => {
        //reset data
        var newItemReset = resetNewItem();
        setNewItem(newItemReset);
        setShowNewItemModal(false);
    }

    const addNewItemModal = (e: any) => {
        e.preventDefault();
        var newItemReset = resetNewItem();
        setNewItem(newItemReset);
        setShowNewItemModal(true);
    }

    const updateNewItemData = (updatedNewItem: NewManualItem) => {
        setNewItem(updatedNewItem);
    }

    const resetNewItem = () => {
        var newItem: NewManualItem = {
            jobId: 0,
            siteAddress: "",
            googleAddress: "",
            googleLatitude: 0,
            googleLongitude: 0,
            customerName: "",
            contactName: "",
            scheduleDate: "",
            scheduleBranchId: 1,
            description: "",
            typeId: -1,
            itemTypeGroupId: -1,
            notes: "",
            runsheetNotes: "",
            errors: {}
        }
        return newItem;
    }

    const saveNewItemData = () => {
        saveNewAllocation();
    }

    const saveNewAllocation = async () => {
        //work out where in the list the job should go
        var truckJobList = props.truckJobList;
        var truckJob = truckJobList[props.truckIndex];
        var jobs = truckJob.jobs;
        var nextIndex = 0;
        var jobsStarted = jobs.filter(j => j.isComplete || j.hasArrived);
        if (jobsStarted.length > 0) {
            //get last job order number
            var lastJob = jobsStarted[jobsStarted.length - 1];
            var lastJobItems = lastJob.items;
            var lastJobItemIndex = lastJobItems.length;
            if (lastJobItemIndex > 0) {
                var lastVehicleOrder = lastJobItems[lastJobItemIndex - 1].vehicleItemOrder;
                nextIndex = lastVehicleOrder + 1;
            } else {
                nextIndex = lastJob.vehicleItemOrder + 1;
            }
        }

        var addItem = {
            siteAddress: newItem.siteAddress,
            googleAddress: newItem.googleAddress,
            googleLatitude: newItem.googleLatitude,
            googleLongitude: newItem.googleLongitude,
            customerName: newItem.customerName,
            contactName: newItem.contactName,
            description: newItem.description,
            typeId: newItem.typeId,
            runsheetNotes: newItem.runsheetNotes,
            truckId: truckJob.truckId,
            orderNumber: nextIndex
        };

        var jsonItem = JSON.stringify(addItem);

        const apiUrl = "SaveDriverManualJob";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            toast.success("New Job Added Successfully");
            var newItemReset = resetNewItem();
            setNewItem(newItemReset);
            setShowNewItemModal(false);
            props.refreshJobs();
        };
            
    }
    //End New Item Modal

    //delete manual driver job
    const cancelDriverManualJob = (e: any, index: number) => {
        e.preventDefault();

        setShowConfirmCancel(true);
        setCancelIndex(index);
    }

    const hideConfirmCancel = () => {
        setShowConfirmCancel(false);
        setCancelIndex(-1);
    }

    const saveConfirmCancel = () => {
        saveCancelJob();
    }

    const saveCancelJob = async () => {
        var truckJobList = props.truckJobList;
        var jobs = truckJobList[props.truckIndex].jobs;
        var job = jobs[cancelIndex];

        let cancelJob = {
            id: job.id
        }

        var jsonItem = JSON.stringify(cancelJob);

        const apiUrl = "DeleteDriverManualJob";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            toast.success("Job Cancelled Successfully");
            setShowConfirmCancel(false);
            setCancelIndex(-1);
            props.refreshJobs();
        };
    }
    //end delete manual driver job

    const downloadPlan = (e: any, id: number) => {
        e.preventDefault();
        getPlanFile(id);
    }

    const getPlanFile = async (id: number) => {
        try {
            var filename = "plans.pdf";
            const apiUrl = 'GetJobFile?Id=' + id;
            await DownloadFile(apiUrl, filename);
        } catch (e) {
            toast.error("Error getting job file!");
        }
    }

    //MOVE DRIVER ADDED JOBS
    const handleDragStart = (event: React.DragEvent<HTMLDivElement>, oldIndex: number) => {
        event.dataTransfer.setData('text', oldIndex.toString());
    }

    const enableDropping = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
    }

    const handleDrop = (event: React.DragEvent<HTMLDivElement>, newIndex: number) => {
        const oldIndexText = event.dataTransfer.getData('text');
        var oldIndex = parseInt(oldIndexText);
        onSortEnd(oldIndex, newIndex);
    }

    const onSortEnd = (oldIndex: number, newIndex: number) => {
        var truckJobList = props.truckJobList;
        var jobs = truckJobList[props.truckIndex].jobs;

        jobs.splice(newIndex, 0, jobs.splice(oldIndex, 1)[0]);

        //loop through the jobs and update the order  
        //for (var i = 0; i < jobs.length; i++) {
        //    //jobs[i]. = (i + 1);
        //}

        props.updateJobs(jobs);
    };
    //END MOVE DRIVER ADDED JOBS

    var hasJobs = props.truckJobList.length > 0;
    var powerlinesText = "Powerlines < 4.6 m";
    let truckJobs = hasJobs ? props.truckJobList[props.truckIndex] : null;
    var lastJobIndex = truckJobs ? truckJobs.jobs.length - 1 : 0;

    let confirmLunch = <ConfirmPopup heading="Lunch Break" text="Stop for a 30 minute lunch break?" hideConfirmModal={hideLunch} showConfirmModal={showLunch} saveConfirmModal={saveLunch} />
    let confirmCancel = <ConfirmPopup heading="Cancel Job" text="Are you sure you want to cancel manually added job?" hideConfirmModal={hideConfirmCancel} showConfirmModal={showConfirmCancel} saveConfirmModal={saveConfirmCancel} />
    let addItem = <AllocateAddItemModal systemCompanyId={2} newItem={newItem} showNewItemModal={showNewItemModal} scheduleTypes={props.scheduleTypes} itemTypeGroups={[]} driverJob={true} hideModal={hideNewItemModal} updateData={updateNewItemData} save={saveNewItemData} />
    let uploadPhotos = <DriverJobsPhotosModal jobId={jobId} showModal={showPhotoImport} hideModal={hideUploadPhotos} />

    let truckNote = "Complete Truck and Tool Check before Departing Office";

    const renderDetails = (
        <div>
            <div className="static-modal">
                {confirmLunch}
                {confirmCancel}
                {addItem}
                {uploadPhotos}
            </div>

            <label className={props.truckOptions.length < 2 ? "hidden" : "input-group makeitflexspacebetween marginBottom10 bg-light"} htmlFor="truckFilterId">
                <span className="label-mobile--inline-sm margin5">Truck</span>
                <select className="select margin5" id="truckId" name="truckId" value={props.truckId} onChange={(e) => updateTruck(e)} disabled={truckJobs != null && truckJobs.hasDepartedOffice && !truckJobs.hasArrivedOffice}>
                    <option hidden defaultValue="-1"></option>
                    {props.truckOptions.map(truck =>
                        <option key={truck.id} value={truck.id}>{truck.name}</option>
                    )};
                </select>
            </label>
            <div className={hasJobs ? "hidden" : ""}>There are no jobs assigned to you for today!</div>
            <div className={truckJobs && props.truckIndex >= 0 ? "" : "hidden"}>
                <div className="makeitflexspacebetween makeitcentervertical">
                    <h2 className="mobile-margin">Jobs</h2>
                    <div className={!hasJobs || truckJobs?.hasDepartedOffice ? "hidden" : "marginBottom10"}>
                        <button className="mobile-button--large" type="button" onClick={departOffice} disabled={!truckJobs?.truckCheckComplete || !truckJobs?.truckToolCheckComplete} >Depart Office</button>
                    </div>
                    <div className={truckJobs?.hasDepartedOffice && truckJobs?.allJobsComplete && !truckJobs?.hasArrivedOffice ? "marginBottom10" : "hidden"}>
                        <button className="mobile-button--large" type="button" onClick={arriveOffice}>Arrive Office</button>
                    </div>
                    <div className={truckJobs?.hasDepartedOffice && hasJobs && !truckJobs?.hadLunch ? "marginBottom10" : "hidden"}>
                        <button className="mobile-button--large" type="button" onClick={addLunch}>Lunch</button>
                    </div>
                </div>
                <div>
                    <span className={!hasJobs ? "" : !truckJobs?.truckCheckComplete || !truckJobs?.truckToolCheckComplete ? "makeitred" : ""}>{!truckJobs?.truckCheckComplete || !truckJobs?.truckToolCheckComplete ? truckNote : ""}</span>
                </div>
                <div className="makeitflexspacebetween makeitcentervertical">
                    <span>Version 1.1</span>
                    <i className={hasJobs ? "mobile-icon-green marginLeft10 fas fa-circle-plus mobileMenuSize" : ""} onClick={(e) => addNewItemModal(e)}></i>
                </div>
                <div className={!hasJobs || truckJobs?.truckNotes === "" ? "hidden" : ""}>
                    <hr className="hr--no-margin"></hr>
                    <div>{truckJobs?.truckNotes}</div>
                </div>

                <hr className="hr--no-margin"></hr>
                <div className={!hasJobs ? "hidden" : "" }>
                    {truckJobs?.jobs.map((job, index) =>
                        <div key={index} id={job.isDriverJob ? "drag" : ""} draggable={job.isDriverJob} onDragStart={(e) => handleDragStart(e, index)} onDragOver={enableDropping} onDrop={(e) => handleDrop(e, index)}>
                            <div className="makeitflexspacebetween makeitcentervertical">
                                <div className="driverjobs-min20">
                                    {index + 1}
                                </div>
                                <div className="makeitbold marginRight10 driverjobs-address">
                                    {job.siteAddress}
                                    <a className={job.isDriverJob ? "hidden" : "marginLeft10"} href={job.siteAddressGoogle} target="_blank" >
                                        <span className="fas fa-map-marker-alt edit--icon"></span>
                                    </a>
                                    <a className={job.isDriverJob && job.isManualJob && !job.hasArrived && !job.isComplete ? "marginLeft10" : "hidden"} href="#" onClick={(e) => cancelDriverManualJob(e, index)} >
                                        <span className="makeitred fas fa-times-circle edit--icon"></span>
                                    </a>
                                    <span className={!job.isManualJob && job.isComplete ? "marginLeft10" : "hidden"}>
                                        <i className="fa-solid fa-camera edit--icon--large makeitdashgreen" onClick={(e) => showUploadPhotos(e, index)}></i>
                                    </span>
                                </div>
                            </div>
                            <div className={job.isComplete ? "hidden" : "makeitflexspacebetween makeitcentervertical"}>
                                <div>
                                    {job.customer}
                                </div>
                                <div className="makeitbold">
                                    <span className={job.gateCode ? "" : "hidden"}>g/c: </span>{job.gateCode}
                                </div>
                            </div>
                            <div className={job.isComplete ? "hidden" : "makeitflexspacebetween makeitcentervertical"}>
                                <div>
                                    {job.siteContact}
                                </div>
                                <div className={job.siteHasIssues ? "" : "hidden"}>
                                    <i className="fas fa-ban edit--icon--large makeitred" onClick={(e) => showSiteIssues(e, index)}></i>
                                </div>
                                <div className={job.isManualJob ? "hidden" : ""}>
                                    <i className="fa-solid fa-camera edit--icon--large makeitdashgreen" onClick={(e) => showUploadPhotos(e, index)}></i>
                                </div>
                            </div>
                            <div className={job.showSiteIssues && !job.isComplete ? "makeitred" : "hidden"}>
                                <div className={job.truckAccess ? "hidden" : ""}>No Truck Access</div>
                                <div className={job.parking ? "hidden" : ""}>No Parking Available</div>
                                <div className={job.siteClean ? "hidden" : ""}>Site is not clean</div>
                                <div className={job.powerlinesWithinRange ? "" : "hidden"}> {powerlinesText}</div>
                            </div>
                            <div className={job.isComplete ? "hidden" : ""}>
                                {job.items.map((item, index) =>
                                    <div key={index} className="makeitflexspacebetween">
                                        <div>
                                            {item.unitNumberDisplay === "" ? "" : item.unitNumberDisplay + " - "}
                                            {item.type}
                                        </div>
                                        <div>
                                            {item.item}
                                        </div>
                                        <div>
                                            {item.itemQuantityType}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className={job.isComplete ? "hidden" : "makeititalic driverjobs--notes"}>
                                <ul>
                                    {job.jobNotes.map((note, index) => 
                                        <div key={index} className={note =="" ? "hidden" : ""}><li>{note}</li></div>
                                    )}
                                </ul>
                            </div>
                            <div className={job.isComplete ? "hidden" : "alignitemscentre"}>
                                <div className={job.plans.length > 0 ? "" : "hidden"}>
                                    {job.plans.map(plan =>
                                        <div key={plan.id}>
                                            <a href="#" onClick={(e) => downloadPlan(e, plan.id)}>{plan.fileName}</a>
                                        </div>
                                    )}

                                </div>
                            </div>
                            <div>
                                <button className={job.hasArrived || job.isComplete || !truckJobs?.hasDepartedOffice || index > 0 && !truckJobs ?.jobs[index - 1].isComplete ? "hidden" : "mobile-button--fill mobile-button--green"} type="button" onClick={(e) => arriveSite(e, index)}>Arrived</button>
                                <button className={((lastJobIndex === index && truckJobs?.truckFinalToolCheckComplete) || lastJobIndex !== index) && job.hasArrived && !job.isComplete ? "mobile-button--fill mobile-button--green" : "hidden"} type="button" onClick={(e) => departSite(e, index)}>Departing</button>
                                <button className={lastJobIndex === index && !truckJobs?.truckFinalToolCheckComplete && job.hasArrived && !job.isComplete ? "mobile-button--fill mobile-button--green" : "hidden"} type="button" onClick={(e) => goToolCheck(e)}>Tool Check</button>
                            </div>
                            <hr className="hr--no-margin"></hr>
                        </div>
                    )}
                </div>
                <div className="makeitflexspacebetween">
                    <label className="input-group" htmlFor="estimatedSiteTimeFormatted">
                        <span className="label-mobile--inline">Est. Site Time:</span>
                        {truckJobs?.estimatedSiteTimeFormatted}
                    </label>
                    <label className="input-group" htmlFor="actualSiteTimeFormatted">
                        <span className="label-mobile--inline">Act. Site Time: </span>
                        {truckJobs?.actualSiteTimeFormatted}
                    </label>
                </div>
                <div className="makeitflexspacebetween">
                    <label className="input-group" htmlFor="estimatedSiteTimeFormatted">
                        <span className="label-mobile--inline">Est. Travel Time:</span>
                        {truckJobs?.estimatedTravelTimeFormatted}
                    </label>
                    <label className="input-group" htmlFor="actualSiteTimeFormatted">
                        <span className="label-mobile--inline">Act. Travel Time: </span>
                        {truckJobs?.actualTravelTimeFormatted}
                    </label>
                </div>
            </div>
        </div>
    );

    return <div>
        {renderDetails}
    </div>;

}

export default DriverJobsListData;